import type { FC } from 'react';
import React, { useState } from 'react';
import cn from 'classnames';
import { Grid } from '@alfa-bank/site-grid-universal';
import { FormInput } from '@alfa-bank/site-form-input';
import { PlateDesktop as Plate } from '@alfa-bank/site-plate/desktop';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { Badge } from '@alfalab/core-components-badge';
import { AlertCircleMIcon } from '@alfalab/icons-glyph/AlertCircleMIcon';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import { isError } from '@alfa-bank/site-form-helpers/meta/is-error';

import { validateNotEmpty } from '../helpers';
import { TimeChipsDesktop } from '../components/time-chips/desktop';
import type { TTimeSlots } from '../models';
import styles from '../styles/styles.css';
import { DateChipsDesktop } from '../components/date-chips/desktop';
import { COMMENT_MAX_LENGTH } from '../../../config';

export const DaySlots: FC<TTimeSlots> = ({
    comment,
    dates,
    intervals,
    onSelectDate,
    onSelectTime,
    selectedDate,
    selectedTime,
    showAttention = false,
}) => {
    const [chosenSlot, setChosenSlot] = useState<string>('');
    const handleClickDateChips = (date: number) => () => {
        onSelectDate(date);
    };

    const handleClickTimeChips = (time: number) => () => {
        onSelectTime(time);
        const { slotFrom, slotTo } = intervals[time];
        setChosenSlot(`${slotFrom}-${slotTo}`);
    };

    return (
        <>
            <Field
                data={selectedDate}
                initialValue={0}
                name="selectedDate"
                render={({ input }) => (
                    <Grid
                        className={cn(styles.external, styles.timesWrapper)}
                        colAutoWidth
                        collapse
                        data-test-id="appointment-form-timeslot-date"
                        justify="start"
                        styler={{ marginBottom: 16 }}
                        wrap
                    >
                        {dates.map((date, idx) => {
                            return (
                                <DateChipsDesktop
                                    key={date}
                                    active={input.value === idx}
                                    date={date}
                                    onClick={handleClickDateChips(idx)}
                                />
                            );
                        })}
                    </Grid>
                )}
            />
            <Field
                data={selectedTime}
                name="selectedTime"
                render={({ input, meta }) => (
                    <div
                        className={cn(styles.external, {
                            [styles.error]: isError(meta),
                        })}
                    >
                        <Grid
                            className={cn(styles.external, styles.timesWrapper)}
                            colAutoWidth
                            collapse
                            data-test-id="appointment-form-timeslot-time"
                            justify="start"
                            wrap
                        >
                            {intervals.map(({ isAvailable, slotFrom, slotTo }, idx) => {
                                return (
                                    <TimeChipsDesktop
                                        key={slotFrom}
                                        active={input.value?.toString() === idx.toString()}
                                        disabled={!isAvailable}
                                        onClick={handleClickTimeChips(idx)}
                                        timeSlot={`${slotFrom}-${slotTo}`}
                                    />
                                );
                            })}
                        </Grid>

                        {isError(meta) && (
                            <Text
                                kind="red"
                                styler={{ marginTop: 12, marginBottom: 0 }}
                                view="component-secondary"
                            >
                                Поле обязательно для заполнения
                            </Text>
                        )}
                    </div>
                )}
                validate={validateNotEmpty()}
            />
            {showAttention && (
                <Plate
                    className={cn(styles.external, styles.attention)}
                    leftAddons={
                        <Badge
                            className={cn(styles.external, styles.badge)}
                            content={<AlertCircleMIcon />}
                            iconColor="attention"
                            view="icon"
                        />
                    }
                    styler={{ marginTop: 24 }}
                    title={`Время ${chosenSlot} больше недоступно. Пожалуйста, выберите другое`}
                    titleView="light"
                    view="attention"
                />
            )}

            {/** ставим явную проверку на false, чтобы по умолчанию поле показывалось для обратной совместимости */}
            {comment?.show === false ? null : (
                <FormInput
                    name="comment"
                    settings={{
                        placeholder: 'Комментарий для сотрудника банка',
                        maxLength: COMMENT_MAX_LENGTH,
                    }}
                    styler={{ marginTop: 24 }}
                />
            )}
        </>
    );
};
